import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import logo from "@/assets/logo.webp";
import { Button } from "@/components/ui/button";

const BOOKING_URL = "https://booking.wellnessacademyusa.com/booking-page-wellness-academy";

const programLinks = [
  { label: "Nutrition & Culinary Wellness", to: "/nutrition" },
  { label: "Physical Activity Training", to: "/physical-activity-training" },
];

const Navbar = () => {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [programsOpen, setProgramsOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > window.innerHeight * 0.85);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setProgramsOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => location.pathname === path;
  const isProgramActive = programLinks.some((l) => isActive(l.to));

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-500",
        scrolled ? "bg-nav shadow-lg shadow-black/20" : "bg-transparent"
      )}
    >
      {/* Utility row */}
      <div className="hidden md:block border-b border-nav-text/10">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 flex items-center justify-end gap-6 h-9">
          <a href="tel:+13104977904" className="font-sans text-xs text-nav-text-muted hover:text-nav-text transition-colors tracking-wide">
            +1 310-497-7904
          </a>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="font-sans text-xs text-nav-text-muted hover:text-nav-text transition-colors tracking-wide">
            Book a Consultation
          </a>
        </div>
      </div>

      {/* Main nav bar */}
      <div className="max-w-6xl mx-auto px-6 sm:px-8 flex items-center justify-between h-16">
        <Link to="/" className="shrink-0 absolute left-1/2 -translate-x-1/2 lg:static lg:left-auto lg:translate-x-0">
          <img src={logo} alt="Wellness Academy USA" width={600} height={80} className="h-12 sm:h-16 lg:h-20 w-auto max-w-[50vw]" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8 text-base">
          <Link to="/about" className={cn("font-sans text-base transition-colors duration-300", isActive("/about") ? "text-nav-cta" : "text-nav-text-muted hover:text-nav-cta")}>About</Link>

          <div className="relative group" onMouseEnter={() => setProgramsOpen(true)} onMouseLeave={() => setProgramsOpen(false)}>
            <button className={cn("font-sans text-base transition-colors duration-300 flex items-center gap-1", isProgramActive ? "text-nav-cta" : "text-nav-text-muted hover:text-nav-cta")} onClick={() => setProgramsOpen(!programsOpen)}>
              Programs
              <ChevronDown size={14} className={cn("transition-transform", programsOpen && "rotate-180")} />
            </button>
            {programsOpen && (
              <div className="absolute top-full left-0 pt-2 w-56">
                <div className="bg-nav rounded-lg border border-nav-text/10 shadow-xl py-2">
                  {programLinks.map((link) => (
                    <Link key={link.to} to={link.to} className={cn("block px-4 py-2.5 font-sans text-base transition-colors duration-300", isActive(link.to) ? "text-nav-cta bg-nav-text/5" : "text-nav-text-muted hover:text-nav-cta hover:bg-nav-text/5")}>
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <Link to="/retreats" className={cn("font-sans text-base transition-colors duration-300", isActive("/retreats") ? "text-nav-cta" : "text-nav-text-muted hover:text-nav-cta")}>Retreats</Link>
          <Link to="/blog" className={cn("font-sans text-base transition-colors duration-300", location.pathname.startsWith("/blog") ? "text-nav-cta" : "text-nav-text-muted hover:text-nav-cta")}>Blog</Link>
          <Link to="/contact" className={cn("font-sans text-base transition-colors duration-300", isActive("/contact") ? "text-nav-cta" : "text-nav-text-muted hover:text-nav-cta")}>Contact</Link>
        </nav>

        <div className="flex items-center gap-4">
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="hidden lg:inline-flex font-sans text-sm font-medium uppercase tracking-widest border border-nav-cta text-nav-cta rounded-full px-5 py-2 hover:bg-nav-cta/10 transition-colors">
            Book a Consultation
          </a>
          <Button variant="ghost" size="icon" className="lg:hidden h-11 w-11 text-nav-text hover:bg-nav-text/10 hover:text-nav-text" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Toggle menu" aria-expanded={mobileOpen} aria-controls="mobile-navigation">
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </Button>
        </div>
      </div>

      {mobileOpen && (
        <div id="mobile-navigation" className="lg:hidden max-h-[calc(100dvh-4rem)] md:max-h-[calc(100dvh-6.25rem)] overflow-y-auto overscroll-contain bg-nav border-t border-nav-text/10">
          <div className="max-w-6xl mx-auto px-6 py-6 space-y-1">
            <Link to="/about" className={cn("block py-3 font-sans text-base transition-colors duration-300", isActive("/about") ? "text-nav-cta" : "text-nav-text-muted hover:text-nav-cta")}>About</Link>
            <button className={cn("w-full flex items-center justify-between py-3 font-sans text-base transition-colors duration-300", isProgramActive ? "text-nav-cta" : "text-nav-text-muted hover:text-nav-cta")} onClick={() => setProgramsOpen(!programsOpen)}>
              Programs
              <ChevronDown size={14} className={cn("transition-transform", programsOpen && "rotate-180")} />
            </button>
            {programsOpen && (
              <div className="pl-4 space-y-1">
                {programLinks.map((link) => (
                  <Link key={link.to} to={link.to} className={cn("block py-2 font-sans text-base transition-colors duration-300", isActive(link.to) ? "text-nav-cta" : "text-nav-text-muted hover:text-nav-cta")}>{link.label}</Link>
                ))}
              </div>
            )}
            <Link to="/retreats" className={cn("block py-3 font-sans text-base transition-colors duration-300", isActive("/retreats") ? "text-nav-cta" : "text-nav-text-muted hover:text-nav-cta")}>Retreats</Link>
            <Link to="/blog" className={cn("block py-3 font-sans text-base transition-colors duration-300", location.pathname.startsWith("/blog") ? "text-nav-cta" : "text-nav-text-muted hover:text-nav-cta")}>Blog</Link>
            <Link to="/contact" className={cn("block py-3 font-sans text-base transition-colors duration-300", isActive("/contact") ? "text-nav-cta" : "text-nav-text-muted hover:text-nav-cta")}>Contact</Link>
            <div className="pt-4 space-y-3">
              <a href="tel:+13104977904" className="block py-3 text-center font-sans text-sm text-nav-text-muted">+1 310-497-7904</a>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="block text-center font-sans text-sm font-medium uppercase tracking-widest border border-nav-cta text-nav-cta rounded-full px-5 py-3 hover:bg-nav-cta/10 transition-colors">
                Book a Consultation
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
